import { isNil } from 'ramda';
import { z } from 'zod';
import { Category, serverCategorySchema, toClientCategory } from './categories/categories.model';
import { Role, serverRoleSchema, toClientRole } from './roles/roles.model';
import { serverUserSchema, User } from './user.model';

/** Server Schemas & Inferred Models **/
export const ServerAssignmentTypesList = ['NONE', 'NOW', 'LATER'] as const;
export type AssignmentTypes = (typeof ServerAssignmentTypesList)[number];
export const serverManagedRelationshipSchema = z
  .object({
    id: z.number(),
    relationship: z.object({ id: z.number(), name: z.string() }),
    categories: z.array(serverCategorySchema).optional(),
    roles: z.array(serverRoleSchema).optional(),
    categoryAssignmentType: z.enum(ServerAssignmentTypesList).optional(),
    roleAssignmentType: z.enum(ServerAssignmentTypesList).optional(),
  })
  .superRefine((data, ctx) => {
    // The schema refinement sets data expectations that some fields are required based on the inclusion of other fields.
    // For instance, we expect categoryAssignmentType to be sent when categories are included in the response etc.
    if (!isNil(data.categories) && isNil(data.categoryAssignmentType)) {
      ctx.addIssue({
        code: 'custom',
        message: 'categoryAssignmentType is expected to be included in response when categories are present',
        path: ['categoryAssignmentType'],
      });
    }

    if (!isNil(data.roles) && isNil(data.roleAssignmentType)) {
      ctx.addIssue({
        code: 'custom',
        message: 'roleAssignmentType is expected to be included in response when roles are present',
        path: ['roleAssignmentType'],
      });
    }
  });

export const serverManagedAreaSchema = z.object({
  id: z.number(),
  name: z.string(),
  relationships: z.array(serverManagedRelationshipSchema).optional(),
  standaloneRoles: z.array(serverRoleSchema).optional(),
  areaManagers: z.array(serverUserSchema).optional(),
});

export type ServerManagedArea = z.infer<typeof serverManagedAreaSchema>;
export type ServerManagedAreaRelationship = z.infer<typeof serverManagedRelationshipSchema>;

/** Client Models **/
export interface ManagedArea {
  id: number;
  name: string;
  relationships?: ManagedAreaRelationship[];
  standaloneRoles?: Role[];
  areaManagers?: User[];
}

export interface ManagedAreaRelationship {
  id: number;
  relationship: { id: number; name: string };
  categories?: Category[];
  roles?: Role[];
  categoryAssignmentType?: AssignmentTypes;
  roleAssignmentType?: AssignmentTypes;
}

/** Client/Server Model Converters **/
export const toManagedAreaRelationship = (
  serverManagedAreaRelationship: ServerManagedAreaRelationship,
): ManagedAreaRelationship => ({
  id: serverManagedAreaRelationship.id,
  relationship: serverManagedAreaRelationship.relationship,
  roles: serverManagedAreaRelationship.roles?.map(toClientRole),
  categories: serverManagedAreaRelationship.categories?.map(toClientCategory),
  roleAssignmentType: serverManagedAreaRelationship.roleAssignmentType,
  categoryAssignmentType: serverManagedAreaRelationship.categoryAssignmentType,
});

export const toManagedArea = (serverManagedArea: ServerManagedArea): ManagedArea => ({
  id: serverManagedArea.id,
  name: serverManagedArea.name,
  relationships: serverManagedArea.relationships?.map(toManagedAreaRelationship),
  standaloneRoles: serverManagedArea.standaloneRoles,
  areaManagers: serverManagedArea.areaManagers,
});
